// Sprint 0083 — Configuration completeness dashboard.
// Shows management whether HR is ready for full implementation.

import { App, Alert, Button, Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd'
import {
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { completenessApi, type CompletenessCheck } from '@/features/hr/configApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title } = Typography

const STATUS_COLOR: Record<string, string> = {
  READY: '#52c41a',
  WARNING: '#fa8c16',
  BLOCKED: '#cf1322',
}
const STATUS_DESCRIPTION: Record<string, string> = {
  READY: 'All checks passed. HR is ready for full implementation.',
  WARNING: 'Critical checks passed but warnings exist. Resolve before go-live.',
  BLOCKED: 'Blocking checks failed. Do not enable full implementation until fixed.',
}

function CheckRow({ check }: { check: CompletenessCheck }) {
  const icon = check.ok
    ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
    : check.severity === 'BLOCKING'
      ? <CloseCircleOutlined style={{ color: '#cf1322', fontSize: 18 }} />
      : <WarningOutlined style={{ color: '#fa8c16', fontSize: 18 }} />

  return (
    <List.Item>
      <List.Item.Meta
        avatar={icon}
        title={
          <Space>
            <Text strong={!check.ok}>{check.name}</Text>
            {!check.ok && (
              <Tag color={check.severity === 'BLOCKING' ? 'red' : 'orange'}>
                {check.severity}
              </Tag>
            )}
          </Space>
        }
        description={<Text type={check.ok ? 'secondary' : 'warning'}>{check.detail}</Text>}
      />
    </List.Item>
  )
}

export default function ConfigCompletenessPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['hr-config-completeness'],
    queryFn: () => completenessApi.check(),
    refetchOnWindowFocus: true,
  })

  const refresh = () => {
    refetch()
    qc.invalidateQueries({ queryKey: ['hr-config-completeness'] })
    message.success('Re-running checks…')
  }

  const status = data?.status ?? 'BLOCKED'

  return (
    <div>
      <PageHeader
        title="HR Configuration Completeness"
        subtitle="17 readiness checks that determine whether HR is fully configured for go-live."
        extra={
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={isLoading}>
            Re-run checks
          </Button>
        }
      />

      <Alert
        type={
          status === 'READY' ? 'success'
            : status === 'WARNING' ? 'warning' : 'error'
        }
        showIcon
        message={
          <Title level={4} style={{ margin: 0, color: STATUS_COLOR[status] }}>
            {status}
          </Title>
        }
        description={STATUS_DESCRIPTION[status]}
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Passed"
              value={data?.ok_count ?? 0}
              suffix={`/ ${data?.total_checks ?? 0}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Blocking failures"
              value={data?.blocking_failures ?? 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Warnings"
              value={data?.warning_failures ?? 0}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Overall status"
              value={status}
              valueStyle={{ color: STATUS_COLOR[status] }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Check details">
        <List
          loading={isLoading}
          dataSource={data?.checks ?? []}
          renderItem={(check) => <CheckRow check={check} />}
        />
      </Card>
    </div>
  )
}
